import * as React from "react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { VotingResults } from "../../../../server/results";
import { api } from "../../api";
import { ResultColumn } from "./ResultColumn";
import type { EntryType } from "../CompoPage";
import { RLink } from "../../DesignSystem/RLink";
import { CompoCategoriesClient } from "../../../../src/entity/CompoCategoriesClient";

type IndividualResultsParams = {
  id: string;
  category_id: string;
  type: EntryType;
};

export const IndividualResultsPage = (): JSX.Element => {
  const { id, category_id, type } = useParams<IndividualResultsParams>();
  const [votingResults, setVotingResults] = useState<VotingResults>();

  useEffect(() => {
    let cancelled = false;

    const fetchVotingResults = async () => {
      const results = await api.get<"/api/compo/:id/results">(`/api/compo/${id}/results`);

      if (!api.isToast(results) && !cancelled) {
        setVotingResults(results);
      }
    };

    fetchVotingResults();

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (!votingResults) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-16">
        <div className="animate-pulse">
          <div className="h-10 w-64 rounded bg-slate-700"></div>
          <div className="mt-2 h-6 w-48 rounded bg-slate-700"></div>
        </div>
      </div>
    );
  }

  const categoryIndex = Number(category_id);
  const associatedCategory = new CompoCategoriesClient(
    votingResults.category_names.filter((category) => category.type === type)[0]
  );

  if (
    Number.isNaN(categoryIndex) ||
    categoryIndex >= votingResults.categories.length ||
    categoryIndex < 0 ||
    !associatedCategory.list[categoryIndex]
  ) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-16">
        <h1 className="text-3xl font-bold text-white">Invalid Category</h1>
        <p className="mt-4 text-slate-400">
          Please tell johnfn if you followed a legitimate link and are not just trolling around.
        </p>
      </div>
    );
  }

  const entries = votingResults.categories[categoryIndex];
  const entriesForType = entries.filter((entry) => entry.entry.type === type);
  const categoryName = associatedCategory.list[categoryIndex];

  return (
    <div className="mx-auto max-w-4xl px-6 py-16">
      <div className="mb-8">
        <RLink
          to={`/compo/${id}/results`}
          className="inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-emerald-400"
        >
          <i className="fa fa-arrow-left" aria-hidden="true"></i>
          Back to full results
        </RLink>
      </div>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white lg:text-4xl">
          Top <span className="text-emerald-400">{type}</span> entries in{" "}
          <span className="text-emerald-400">{categoryName}</span>
        </h1>
        <p className="mt-2 text-lg text-slate-400">{votingResults.compo.name}</p>
      </div>

      <ResultColumn
        entries={entriesForType}
        categoryName={categoryName}
        entryUsers={votingResults.entry_users}
        fullWidth={true}
      />
    </div>
  );
};